/**
 * The one place the Firebase client app is configured and created — T007.
 *
 * Why this module exists at all: `src/lib/api.ts` (callables) and `src/auth/` (Google sign-in) both
 * need the *same* `FirebaseApp` instance, because the callable transport takes the ID token from the
 * Auth instance attached to that app. Two apps would mean calls made as nobody. So the app is created
 * once here, lazily, and both consumers ask this module for it.
 *
 * Boundaries (`BOUNDARIES.md` § 2 and § 4):
 *   - this module imports `firebase/app` only. `firebase/functions` belongs to `api.ts` alone (B2),
 *     and `firebase/firestore` / `firebase/storage` exist nowhere in the client at all (B1);
 *   - no project id, API key, bucket or domain is written in this repository. Every value comes from
 *     the build's `VITE_…` environment, so the same bundle is configured per environment (D-11).
 *
 * Missing configuration is a first-class, visible state rather than a crash: `readFirebaseConfig`
 * reports exactly which variables are absent, the shell renders that in Georgian, and **nothing is
 * ever sent anywhere** — no app is initialized, so no network call can be made.
 */
import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';

/** VERIFIED (`ARCHITECTURE.md` § 12): every v2 resource, callables included, lives in `europe-west1`. */
export const FUNCTIONS_REGION = 'europe-west1';

/**
 * The four values the client genuinely needs. Deliberately not the full Firebase config object:
 * `storageBucket` and `messagingSenderId` serve products this SPA never loads (B1 — the client has
 * no Storage path, and there is no messaging anywhere in the accepted architecture), and asking an
 * operator for a value nothing reads is how stale configuration is born.
 */
export const REQUIRED_ENV_VARS = Object.freeze([
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const);

export type RequiredEnvVar = (typeof REQUIRED_ENV_VARS)[number];

export interface FirebaseWebConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
}

export type FirebaseConfigResult =
  | { readonly configured: true; readonly config: FirebaseWebConfig }
  | { readonly configured: false; readonly missing: readonly RequiredEnvVar[] };

/** `true` when the build asks for the local Firebase Emulator Suite (`infra/firebase.json`). */
export const EMULATOR_ENV_VAR = 'VITE_FIREBASE_EMULATORS';

/** The emulator ports of `infra/firebase.json`; kept beside the flag so the two cannot drift apart. */
export const EMULATOR_HOSTS = Object.freeze({
  auth: 'http://127.0.0.1:9099',
  functionsHost: '127.0.0.1',
  functionsPort: 5001,
});

function readString(env: Readonly<Record<string, unknown>>, name: string): string | null {
  const raw = env[name];
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  return value.length > 0 ? value : null;
}

/**
 * Pure: read a configuration out of an environment record, or report exactly what is absent.
 *
 * A variable that is present but empty or whitespace counts as absent. That is not pedantry — an
 * empty `VITE_FIREBASE_PROJECT_ID` is what a mis-set CI variable looks like, and letting it through
 * would produce an app that initializes and then fails on every call with an opaque error.
 */
export function readFirebaseConfig(env: Readonly<Record<string, unknown>>): FirebaseConfigResult {
  const missing = REQUIRED_ENV_VARS.filter((name) => readString(env, name) === null);
  if (missing.length > 0) {
    return { configured: false, missing: Object.freeze(missing) };
  }
  return {
    configured: true,
    config: Object.freeze({
      apiKey: readString(env, 'VITE_FIREBASE_API_KEY') as string,
      authDomain: readString(env, 'VITE_FIREBASE_AUTH_DOMAIN') as string,
      projectId: readString(env, 'VITE_FIREBASE_PROJECT_ID') as string,
      appId: readString(env, 'VITE_FIREBASE_APP_ID') as string,
    }),
  };
}

/**
 * Truthy only for the explicit opt-in values, so a stray `"false"` never points a build at localhost —
 * and only for a Firebase demo project (`demo-…`, which the emulators alone serve): a build carrying a
 * cloud project id never talks to a local emulator, whatever the flag says (R01).
 */
export function emulatorsEnabled(env: Readonly<Record<string, unknown>>): boolean {
  return emulatorFlagSet(env) && (readString(env, 'VITE_FIREBASE_PROJECT_ID') ?? '').startsWith('demo-');
}

/** Whether the build asked for emulators at all — what must keep a build out of production paths. */
export function emulatorFlagSet(env: Readonly<Record<string, unknown>>): boolean {
  const value = readString(env, EMULATOR_ENV_VAR);
  return value === '1' || value === 'true';
}

/** Thrown by `getFirebaseApp()` when the build carries no configuration. Callers render, never crash. */
export class FirebaseConfigurationError extends Error {
  readonly missing: readonly RequiredEnvVar[];

  constructor(missing: readonly RequiredEnvVar[]) {
    super(`Firebase web configuration is incomplete: ${missing.join(', ')}`);
    this.name = 'FirebaseConfigurationError';
    this.missing = Object.freeze([...missing]);
  }
}

/** The build's own environment. Read through a function so tests can exercise the pure form above. */
export function currentEnv(): Readonly<Record<string, unknown>> {
  return import.meta.env as unknown as Readonly<Record<string, unknown>>;
}

export function firebaseConfigStatus(): FirebaseConfigResult {
  return readFirebaseConfig(currentEnv());
}

let app: FirebaseApp | null = null;

/**
 * The single `FirebaseApp`, created on first use.
 *
 * Lazy on purpose: an unconfigured build must be able to render its configuration screen without
 * `initializeApp` ever running, and a signed-out visitor should not pay for SDK initialization
 * before they press the sign-in button.
 */
export function getFirebaseApp(): FirebaseApp {
  if (app !== null) return app;
  const status = firebaseConfigStatus();
  if (!status.configured) {
    throw new FirebaseConfigurationError(status.missing);
  }
  const existing = getApps();
  app = existing.length > 0 ? (existing[0] as FirebaseApp) : initializeApp({ ...status.config });
  return app;
}
